import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { CheckCircle, User, Building2, Phone, Mail, FileText, Loader2, ArrowLeft, MapPin, Navigation, AlertCircle, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MRKioskPage() {
  const { clinicCode } = useParams();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkinId, setCheckinId] = useState(null);
  const [step, setStep] = useState('form'); // form, success, checkout
  
  // Vendor selection state
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [loadingVendors, setLoadingVendors] = useState(false);
  
  // Location state
  const [location, setLocation] = useState({ latitude: null, longitude: null, address: '', loading: false, error: null });
  const [locationResult, setLocationResult] = useState(null);
  
  const [form, setForm] = useState({
    mr_name: '',
    phone: '',
    email: '',
    vendor_type: '',
    vendor_id: '',
    vendor_name: '',
    purpose: '',
    products_discussed: ''
  });

  useEffect(() => {
    fetchClinicInfo();
  }, [clinicCode]);

  // Fetch vendors when vendor_type changes
  useEffect(() => {
    if (form.vendor_type) {
      fetchVendors(form.vendor_type);
    } else {
      setVendors([]);
    }
  }, [form.vendor_type]);

  // Request location on mount
  useEffect(() => {
    requestLocation();
  }, []);

  const fetchClinicInfo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/mr-checkin/clinic/${clinicCode}`);
      if (res.ok) {
        const data = await res.json();
        setClinic(data);
      } else {
        toast.error('Clinic not found. Please check the code.');
      }
    } catch (err) {
      toast.error('Failed to fetch clinic info');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async (type, search = '') => {
    setLoadingVendors(true);
    try {
      const params = new URLSearchParams();
      if (type) params.append('vendor_type', type);
      if (search) params.append('search', search);
      
      const res = await fetch(`${API_URL}/api/mr-checkin/vendors?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setVendors(data);
      }
    } catch (err) {
      console.error('Failed to fetch vendors', err);
    } finally {
      setLoadingVendors(false);
    }
  };

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocation(prev => ({ ...prev, error: 'Geolocation not supported' }));
      return;
    }
    
    setLocation(prev => ({ ...prev, loading: true, error: null }));
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        
        // Get address from coordinates using Nominatim
        let address = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`,
            { headers: { 'User-Agent': 'OneClinique/1.0' } }
          );
          if (res.ok) {
            const data = await res.json();
            address = data.display_name || address;
          }
        } catch (e) {
          console.error('Reverse geocode error:', e);
        }
        
        setLocation({
          latitude: lat,
          longitude: lon,
          address,
          loading: false,
          error: null
        });
      },
      (error) => {
        let errorMsg = 'Failed to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Location permission denied. Please enable location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Location unavailable';
            break;
          case error.TIMEOUT:
            errorMsg = 'Location request timed out';
            break;
          default:
            break;
        }
        setLocation(prev => ({ ...prev, loading: false, error: errorMsg }));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.mr_name.trim() || !form.phone.trim()) {
      toast.error('Please fill in your name and phone number');
      return;
    }
    if (!form.vendor_type || !form.vendor_id) {
      toast.error('Please select vendor type and vendor name');
      return;
    }
    if (!location.latitude || !location.longitude) {
      toast.error('Please enable location access for check-in');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        mr_name: form.mr_name,
        phone: form.phone,
        email: form.email,
        vendor_id: form.vendor_id,
        vendor_type: form.vendor_type,
        purpose: form.purpose,
        products_discussed: form.products_discussed ? form.products_discussed.split(',').map(p => p.trim()).filter(Boolean) : [],
        latitude: location.latitude,
        longitude: location.longitude
      };
      
      const res = await fetch(`${API_URL}/api/mr-checkin/clinic/${clinic.clinic_id}/check-in-v2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        setCheckinId(data.checkin_id);
        setLocationResult({
          verified: data.location_verified,
          distance: data.distance_from_clinic,
          address: data.location_address
        });
        setStep('success');
        toast.success('Check-in successful!');
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed');
      }
    } catch (err) {
      toast.error(err.message || 'Check-in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckout = async () => {
    if (!checkinId) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/mr-checkin/check-out/${checkinId}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Check-out successful!');
        setStep('checkout');
      }
    } catch (err) {
      toast.error('Check-out failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ mr_name: '', phone: '', email: '', vendor_type: '', vendor_id: '', vendor_name: '', purpose: '', products_discussed: '' });
    setCheckinId(null);
    setLocationResult(null);
    setStep('form');
    requestLocation();
  };

  const handleVendorSelect = (vendorId) => {
    const selectedVendor = vendors.find(v => v.id === vendorId);
    setForm({
      ...form,
      vendor_id: vendorId,
      vendor_name: selectedVendor?.name || ''
    });
  };

  // Filter vendors by search
  const filteredVendors = vendors.filter(v => 
    v.name.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Clinic Not Found</h2>
            <p className="text-slate-500">The clinic code "{clinicCode}" is not valid.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4" data-testid="mr-kiosk-page">
      <Card className="max-w-lg w-full shadow-xl">
        <CardHeader className="text-center border-b pb-6">
          {clinic.logo_url && (
            <img src={clinic.logo_url} alt="Clinic Logo" className="w-20 h-20 mx-auto mb-4 rounded-lg object-cover" />
          )}
          <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>{clinic.clinic_name}</CardTitle>
          <CardDescription className="text-base">
            {clinic.doctor_name && <span className="block">{clinic.doctor_name}</span>}
            {clinic.specialty && <Badge variant="secondary" className="mt-2">{clinic.specialty}</Badge>}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="pt-6">
          {step === 'form' && (
            <>
              <h3 className="text-lg font-semibold text-center mb-4 text-purple-700">MR Self Check-In</h3>
              
              {/* Location Status */}
              <div className={`mb-4 p-3 rounded-lg flex items-center gap-3 ${
                location.loading ? 'bg-blue-50 text-blue-700' :
                location.error ? 'bg-red-50 text-red-700' :
                location.latitude ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
              }`}>
                {location.loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Getting your location...</span>
                  </>
                ) : location.error ? (
                  <>
                    <AlertCircle className="w-5 h-5" />
                    <div className="flex-1">
                      <span className="text-sm block">{location.error}</span>
                      <Button size="sm" variant="link" onClick={requestLocation} className="p-0 h-auto text-red-700">
                        Retry
                      </Button>
                    </div>
                  </>
                ) : location.latitude ? (
                  <>
                    <MapPin className="w-5 h-5" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">Location captured</span>
                      <span className="text-xs block opacity-75 truncate">{location.address}</span>
                    </div>
                    <Navigation className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    <MapPin className="w-5 h-5" />
                    <span className="text-sm">Location required for check-in</span>
                    <Button size="sm" variant="outline" onClick={requestLocation}>
                      Enable
                    </Button>
                  </>
                )}
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* MR Details */}
                <div>
                  <Label className="flex items-center gap-2"><User className="w-4 h-4" /> Your Name *</Label>
                  <Input
                    data-testid="mr-kiosk-name"
                    value={form.mr_name}
                    onChange={(e) => setForm({ ...form, mr_name: e.target.value })}
                    placeholder="Enter your full name"
                    className="mt-1"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2"><Phone className="w-4 h-4" /> Phone *</Label>
                    <Input
                      data-testid="mr-kiosk-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+91 98765 43210"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
                    <Input
                      data-testid="mr-kiosk-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="email@company.com"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                {/* Vendor Selection */}
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-700 flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Select Your Company *
                  </h4>
                  
                  <div>
                    <Label>Vendor Type *</Label>
                    <Select 
                      value={form.vendor_type} 
                      onValueChange={(v) => {
                        setForm({ ...form, vendor_type: v, vendor_id: '', vendor_name: '' });
                        setVendorSearch('');
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid="mr-kiosk-vendor-type">
                        <SelectValue placeholder="Select vendor type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lab">Laboratory</SelectItem>
                        <SelectItem value="pharma">Pharmaceutical Company</SelectItem>
                        <SelectItem value="inventory">Medical Equipment / Inventory</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {form.vendor_type && (
                    <div>
                      <Label>Vendor Name *</Label>
                      <div className="relative mt-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Search vendor..."
                          value={vendorSearch}
                          onChange={(e) => setVendorSearch(e.target.value)}
                          className="pl-10"
                          data-testid="mr-kiosk-vendor-search"
                        />
                      </div>
                      
                      {loadingVendors ? (
                        <div className="mt-2 text-center text-slate-500">
                          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                          Loading vendors...
                        </div>
                      ) : (
                        <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg">
                          {filteredVendors.length === 0 ? (
                            <p className="p-3 text-sm text-slate-500 text-center">
                              No vendors found
                            </p>
                          ) : (
                            filteredVendors.map(vendor => (
                              <div
                                key={vendor.id}
                                className={`p-3 cursor-pointer hover:bg-purple-50 border-b last:border-b-0 ${
                                  form.vendor_id === vendor.id ? 'bg-purple-100' : ''
                                }`}
                                onClick={() => handleVendorSelect(vendor.id)}
                                data-testid={`vendor-option-${vendor.id}`}
                              >
                                <p className="font-medium text-sm">{vendor.name}</p>
                                <p className="text-xs text-slate-500">{vendor.email}</p>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                      
                      {form.vendor_name && (
                        <div className="mt-2 p-2 bg-purple-100 rounded-lg flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium text-purple-700">Selected: {form.vendor_name}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Visit Purpose */}
                <div>
                  <Label>Purpose of Visit</Label>
                  <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                    <SelectTrigger className="mt-1" data-testid="mr-kiosk-purpose">
                      <SelectValue placeholder="Select purpose" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product_demo">Product Demo</SelectItem>
                      <SelectItem value="sample_drop">Sample Drop</SelectItem>
                      <SelectItem value="follow_up">Follow Up</SelectItem>
                      <SelectItem value="new_launch">New Product Launch</SelectItem>
                      <SelectItem value="order_discussion">Order Discussion</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="flex items-center gap-2"><FileText className="w-4 h-4" /> Products to Discuss</Label>
                  <Textarea
                    data-testid="mr-kiosk-products"
                    value={form.products_discussed}
                    onChange={(e) => setForm({ ...form, products_discussed: e.target.value })}
                    placeholder="Enter product names, separated by commas"
                    className="mt-1"
                    rows={2}
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-lg"
                  disabled={submitting || !location.latitude}
                  data-testid="mr-kiosk-submit"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle className="w-5 h-5 mr-2" />}
                  {submitting ? 'Checking In...' : 'Check In'}
                </Button>
              </form>
            </>
          )}
          
          {step === 'success' && (
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-semibold text-green-700 mb-2">Check-In Successful!</h3>
              <p className="text-slate-500 mb-4">
                Welcome, {form.mr_name}!<br />
                You are now checked in at {clinic.clinic_name}.
              </p>
              
              {/* Location Verification Status */}
              {locationResult && (
                <div className={`mb-4 p-4 rounded-lg ${locationResult.verified ? 'bg-green-50' : 'bg-yellow-50'}`}>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <MapPin className={`w-5 h-5 ${locationResult.verified ? 'text-green-600' : 'text-yellow-600'}`} />
                    <span className={`font-medium ${locationResult.verified ? 'text-green-700' : 'text-yellow-700'}`}>
                      {locationResult.verified ? 'Location Verified' : 'Location Not Verified'}
                    </span>
                  </div>
                  {locationResult.distance !== null && (
                    <p className="text-sm text-slate-600">
                      Distance from clinic: <strong>{locationResult.distance}m</strong>
                      {!locationResult.verified && <span className="text-yellow-600"> (outside 500m radius)</span>}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-1 truncate">{locationResult.address}</p>
                </div>
              )}
              
              {/* Vendor Info */}
              <div className="mb-6 p-3 bg-slate-50 rounded-lg text-left">
                <p className="text-sm text-slate-500">Representing:</p>
                <p className="font-medium">{form.vendor_name}</p>
                <Badge variant="secondary" className="mt-1 capitalize">{form.vendor_type}</Badge>
              </div>
              
              <div className="space-y-3">
                <Button 
                  onClick={handleCheckout}
                  variant="outline"
                  className="w-full h-12"
                  disabled={submitting}
                  data-testid="mr-kiosk-checkout"
                >
                  {submitting ? 'Processing...' : 'Check Out When Done'}
                </Button>
                <Button 
                  onClick={resetForm}
                  variant="ghost"
                  className="w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> New Check-In
                </Button>
              </div>
            </div>
          )}
          
          {step === 'checkout' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-2xl font-semibold text-blue-700 mb-2">Check-Out Complete!</h3>
              <p className="text-slate-500 mb-6">
                Thank you for visiting {clinic.clinic_name}.<br />
                Have a great day!
              </p>
              <Button 
                onClick={resetForm}
                className="w-full h-12 bg-purple-600 hover:bg-purple-700"
              >
                Start New Check-In
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
